import { Component, OnInit } from "@angular/core";
import { SlimLoadingBarService } from "ng2-slim-loading-bar";
import swal from "sweetalert2";
import { environment } from "../../environments/environment";
import { AuditorPermissionsService } from "../services/shared/auditor-permissions.service";
import { ProjectsService } from "../services/projects/projects.service";
import { ReservationService } from "../services/reservation-service/reservation.service";
import { ErrorHandlerService } from "../services/shared/error-handler.service";

declare var Tafgeet: any;

@Component({
  selector: "app-print-single-cheque",
  templateUrl: "./print-single-cheque.component.html",
  styleUrls: ["./print-single-cheque.component.css"],
})
export class PrintSingleChequeComponent implements OnInit {
  readonly DATE_FORMAT_STANDARD = 1;
  readonly DATE_FORMAT_SPACED_DDMMYYYY = 2;
  readonly DEFAULT_DATE_DIGIT_SPACING_PX = 1;
  readonly defaultLinearText = "يصرف للمستفيد الأول";
  readonly MAX_CHEQUE_WIDTH_CM = 30;
  readonly MAX_CHEQUE_HEIGHT_CM = 15;

  cheque_type: string = "receivable";
  bank_id: any;
  cheque_template_id: any;
  developer_id: any;
  developer_name: string = "";
  amount: any;
  date: string;
  linear_text = this.defaultLinearText;
  print_linear = true;
  banks: any[] = [];
  developers: any[] = [];
  filteredChequeTemplates: any[] = [];
  selectedTemplate: any = null;
  selectedTemplateImageUrl: string = null;
  chequeDimensions: { width: number; height: number } = null;
  hasTemplateDimensions = false;
  is_loading = false;
  isTemplatesLoading = false;
  isTemplateDetailsLoading = false;
  isTemplateImageLoading = false;
  isAuditor = false;

  constructor(
    private slimLoadingBarService: SlimLoadingBarService,
    private errorHandlerService: ErrorHandlerService,
    private projectService: ProjectsService,
    private reservationService: ReservationService,
    private auditorPermissionsService: AuditorPermissionsService
  ) {}

  ngOnInit() {
    this.setDefaultDateToToday();
    this.isAuditor = this.auditorPermissionsService.isAuditor();
    this.getBanks();
    this.getDevelopers();
  }

  private setDefaultDateToToday() {
    var now = new Date();
    var timezoneOffsetMs = now.getTimezoneOffset() * 60000;

    this.date = new Date(now.getTime() - timezoneOffsetMs)
      .toISOString()
      .split("T")[0];
  }

  getBanks() {
    this.slimLoadingBarService.start();
    this.reservationService.getbanks().subscribe(
      (data: any) => {
        this.banks = Array.isArray(data) ? data : data && data.data ? data.data : [];
      },
      (err) => this.errorHandlerService.handleErorr(err),
      () => this.slimLoadingBarService.complete()
    );
  }

  getDevelopers() {
    this.projectService.getDevelopers().subscribe((res: any) => {
      this.developers = Array.isArray(res) ? res : res && res.data ? res.data : [];
    });
  }

  getChequeTemplatesByBank() {
    if (!this.bank_id) {
      this.filteredChequeTemplates = [];
      return;
    }

    this.isTemplatesLoading = true;
    this.reservationService.getChequeTemplatesByBank(this.bank_id).subscribe(
      (res: any) => {
        var templates = Array.isArray(res) ? res : res && res.data ? res.data : [];
        this.filteredChequeTemplates = templates.filter((template) => {
          return String(template.bank_id) === String(this.bank_id);
        });
        this.isTemplatesLoading = false;
      },
      (err) => {
        this.isTemplatesLoading = false;
        this.errorHandlerService.handleErorr(err);
      }
    );
  }

  onBankChange() {
    this.cheque_template_id = null;
    this.selectedTemplate = null;
    this.selectedTemplateImageUrl = null;
    this.chequeDimensions = null;
    this.hasTemplateDimensions = false;
    this.isTemplateImageLoading = false;
    this.resetChequeFormFields();
    this.getChequeTemplatesByBank();
  }

  onTemplateChange() {
    this.resetTemplatePreviewState();

    if (!this.cheque_template_id) {
      return;
    }

    this.isTemplateDetailsLoading = true;
    this.projectService.getBankChequeById(this.cheque_template_id).subscribe(
      (res: any) => {
        this.selectedTemplate = res && res.data ? res.data : res || null;
        this.applyTemplateDimensions(this.selectedTemplate);
        this.applyTemplatePreviewDefaults(this.selectedTemplate);
        this.loadSelectedTemplateImage(this.selectedTemplate);

        var templateLinearText = String(
          this.getNestedValue(this.selectedTemplate, ["variables", "linear", "value"], "")
        ).trim();
        this.linear_text = templateLinearText || this.defaultLinearText;

        var selected = this.getNestedValue(
          this.selectedTemplate,
          ["variables", "linear", "selected"],
          null
        );
        this.print_linear =
          selected === undefined || selected === null ? true : !!selected;
        this.isTemplateDetailsLoading = false;
      },
      (err) => {
        this.isTemplateDetailsLoading = false;
        this.errorHandlerService.handleErorr(err);
      }
    );
  }

  private applyTemplateDimensions(template: any) {
    var variables = this.getNestedValue(template, ["variables"], {});
    var raw =
      this.getNestedValue(variables, ["Dimensions"], null) ||
      this.getNestedValue(variables, ["dimensions"], null) ||
      this.getNestedValue(template, ["Dimensions"], null) ||
      this.getNestedValue(template, ["dimensions"], {}) ||
      {};
    var width =
      this.normalizeDimension(raw.width, this.MAX_CHEQUE_WIDTH_CM) ||
      this.normalizeDimension(raw.witdh, this.MAX_CHEQUE_WIDTH_CM) ||
      this.normalizeDimension(
        this.getNestedValue(template, ["width"], null),
        this.MAX_CHEQUE_WIDTH_CM
      );
    var height =
      this.normalizeDimension(raw.height, this.MAX_CHEQUE_HEIGHT_CM) ||
      this.normalizeDimension(raw.higth, this.MAX_CHEQUE_HEIGHT_CM) ||
      this.normalizeDimension(
        this.getNestedValue(template, ["height"], null),
        this.MAX_CHEQUE_HEIGHT_CM
      );

    this.hasTemplateDimensions = !!(width && height);
    this.chequeDimensions =
      width && height ? { width: width, height: height } : null;
  }

  private applyTemplatePreviewDefaults(template: any) {
    var variables = this.getNestedValue(template, ["variables"], {});
    var templateAmount = String(
      this.getNestedValue(variables, ["amount", "value"], "") || ""
    ).trim();
    var templateDate = String(
      this.getNestedValue(variables, ["date", "raw_value"], "") ||
        this.getNestedValue(variables, ["date", "value"], "") ||
        ""
    ).trim();
    var templateDeveloperName = String(
      this.getNestedValue(variables, ["beneficiary_name", "value"], "") || ""
    ).trim();

    this.amount = templateAmount || "";
    this.date = templateDate || this.date;
    if (this.cheque_type === "payable") {
      this.developer_name = templateDeveloperName;
      this.developer_id = null;
    } else {
      this.developer_id = this.findDeveloperIdByName(templateDeveloperName);
    }
  }

  onChequeTypeChange() {
    this.developer_id = null;
    this.developer_name = "";
  }

  private resetChequeFormFields() {
    this.developer_id = null;
    this.developer_name = "";
    this.amount = "";
    this.setDefaultDateToToday();
    this.linear_text = this.defaultLinearText;
    this.print_linear = true;
  }

  private resetTemplatePreviewState() {
    this.selectedTemplate = null;
    this.selectedTemplateImageUrl = null;
    this.chequeDimensions = null;
    this.hasTemplateDimensions = false;
    this.isTemplateImageLoading = false;
    this.resetChequeFormFields();
  }

  private loadSelectedTemplateImage(template: any) {
    var imageUrl = this.resolveTemplateImageUrl(template);
    var image;

    if (!imageUrl) {
      this.selectedTemplateImageUrl = null;
      this.isTemplateImageLoading = false;
      return;
    }

    this.isTemplateImageLoading = true;
    image = new Image();
    image.onload = () => {
      this.selectedTemplateImageUrl = imageUrl;
      this.isTemplateImageLoading = false;
    };
    image.onerror = () => {
      this.selectedTemplateImageUrl = imageUrl;
      this.isTemplateImageLoading = false;
    };
    image.src = imageUrl;
  }

  private resolveTemplateImageUrl(template: any): string {
    var rawFullUrl = this.getNestedValue(template, ["full_url"], null);
    var relativeUrl = String(this.getNestedValue(template, ["url"], "") || "").trim();
    var normalizedBase = String(environment.api_raw_base_url || "").replace(/\/+$/, "");
    var normalizedPath = relativeUrl && relativeUrl.charAt(0) !== "/" ? "/" + relativeUrl : relativeUrl;

    if (rawFullUrl) {
      return this.normalizeImageUrl(rawFullUrl);
    }

    if (!relativeUrl) {
      return null;
    }

    return this.normalizeImageUrl(normalizedBase + normalizedPath);
  }

  private normalizeImageUrl(url: string): string {
    try {
      return encodeURI(String(url || "").trim());
    } catch (_e) {
      return String(url || "").trim();
    }
  }

  private findDeveloperIdByName(name: string): any {
    var normalizedName = String(name || "").trim().toLowerCase();
    var matchedDeveloper;

    if (!normalizedName || !Array.isArray(this.developers)) {
      return null;
    }

    matchedDeveloper = this.developers.find((developer: any) => {
      return (
        String((developer && developer.name) || "")
          .trim()
          .toLowerCase() === normalizedName
      );
    });

    return matchedDeveloper ? matchedDeveloper.id : null;
  }

  private getNestedValue(source: any, path: string[], fallback?: any): any {
    var value = source;
    var index;

    for (index = 0; index < path.length; index++) {
      if (value === undefined || value === null) {
        return fallback;
      }

      value = value[path[index]];
    }

    return value === undefined || value === null ? fallback : value;
  }

  private toFiniteNumber(value: any): number {
    var normalizedValue = this.normalizeDecimalString(value);
    var normalized = Number(normalizedValue);

    return isFinite(normalized) && normalized > 0 ? normalized : null;
  }

  private normalizeDecimalString(value: any): string {
    var englishDigits = String(value === undefined || value === null ? "" : value)
      .replace(/[\u0660-\u0669]/g, function(c) {
        return String(c.charCodeAt(0) - 0x0660);
      })
      .replace(/[\u06f0-\u06f9]/g, function(c) {
        return String(c.charCodeAt(0) - 0x06f0);
      })
      .replace(/,/g, ".");
    var output = "";
    var hasDot = false;
    var index;
    var ch;

    for (index = 0; index < englishDigits.length; index++) {
      ch = englishDigits.charAt(index);

      if (ch >= "0" && ch <= "9") {
        output += ch;
        continue;
      }

      if (ch === "." && !hasDot) {
        output += ch;
        hasDot = true;
      }
    }

    return output;
  }

  private normalizeDimension(value: any, maxValue: number): number {
    var finiteValue = this.toFiniteNumber(value);

    if (!finiteValue) {
      return null;
    }

    return Math.min(finiteValue, maxValue);
  }

  getChequePreviewStyle() {
    return {
      width:
        ((this.chequeDimensions && this.chequeDimensions.width) || 17.5) + "cm",
      height:
        ((this.chequeDimensions && this.chequeDimensions.height) || 8.2) + "cm",
    };
  }

  getChequeBackgroundStyle() {
    var style = this.getChequePreviewStyle();
    style["background-image"] =
      'url("' + (this.getSelectedTemplateImage() || "") + '")';

    return style;
  }

  getAppliedChequeDimensionsLabel(): string {
    var width =
      (this.chequeDimensions && this.chequeDimensions.width) || 17.5;
    var height =
      (this.chequeDimensions && this.chequeDimensions.height) || 8.2;

    return width + "cm x " + height + "cm";
  }

  getDeveloperName() {
    if (this.cheque_type === "payable") {
      return String(this.developer_name || "").trim();
    }

    var selectedDeveloper = this.developers.find((developer) => {
      return String(developer.id) === String(this.developer_id);
    });

    return selectedDeveloper ? selectedDeveloper.name : "";
  }

  getTemplateBeneficiaryName() {
    var beneficiary = this.getNestedValue(
      this.selectedTemplate,
      ["variables", "beneficiary_name", "value"],
      ""
    );

    return this.getDeveloperName() || beneficiary || "";
  }

  getTemplateFieldStyle(
    fieldName: "date" | "beneficiary_name" | "amount" | "amount_text"
  ) {
    var field = this.getNestedValue(
      this.selectedTemplate,
      ["variables", fieldName],
      null
    );
    var style: any;
    var hasLeft;
    var hasRight;
    var digitSpacing;

    if (!field) {
      return {};
    }

    style = {
      top: field.top || "auto",
      right: field.right || "auto",
      bottom: field.bottom || "auto",
      left: field.left || "auto",
    };

    if (fieldName === "beneficiary_name") {
      hasLeft = style.left && style.left !== "auto";
      hasRight = style.right && style.right !== "auto";

      if (hasLeft && hasRight) {
        style.right = "auto";
      }

      style.bottom = "auto";
    }

    if (fieldName === "date") {
      style["max-width"] = "100px";
      digitSpacing = this.getTemplateDateDigitSpacing();

      if (digitSpacing !== null) {
        style["letter-spacing.px"] = digitSpacing;
      }
    }

    return style;
  }

  isSpacedDateFormat(): boolean {
    return (
      Number(
        this.getNestedValue(
          this.selectedTemplate,
          ["variables", "date", "format"],
          this.DATE_FORMAT_STANDARD
        )
      ) === this.DATE_FORMAT_SPACED_DDMMYYYY
    );
  }

  private getTemplateDateDigitSpacing(): number {
    var rawSpacing = this.getNestedValue(
      this.selectedTemplate,
      ["variables", "date", "digit_spacing"],
      null
    );
    var spacing = Number(rawSpacing);

    if (!isFinite(spacing) || spacing < 0) {
      return null;
    }

    return spacing;
  }

  getSelectedTemplateImage() {
    return this.selectedTemplateImageUrl || this.getNestedValue(this.selectedTemplate, ["full_url"], null);
  }

  getDateForChequePreview(): string {
    var normalizedInputDate = String(this.date || "").trim();
    var templateDate;
    var displayValue;
    var formattedValue;

    if (normalizedInputDate) {
      return this.formatDateByTemplateFormat(
        normalizedInputDate,
        Number(
          this.getNestedValue(
            this.selectedTemplate,
            ["variables", "date", "format"],
            this.DATE_FORMAT_STANDARD
          )
        )
      );
    }

    templateDate = this.getNestedValue(
      this.selectedTemplate,
      ["variables", "date"],
      {}
    );
    displayValue = String(templateDate.display_value || "").trim();
    if (displayValue) {
      return displayValue;
    }

    formattedValue = String(templateDate.formatted_value || "").trim();
    if (formattedValue) {
      return formattedValue;
    }

    return String(templateDate.value || "").trim();
  }

  private formatDateByTemplateFormat(value: string, format: number): string {
    var isoMatch;
    var yyyy;
    var mm;
    var dd;

    if (format !== this.DATE_FORMAT_SPACED_DDMMYYYY) {
      return value;
    }

    isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!isoMatch) {
      return value;
    }

    yyyy = isoMatch[1];
    mm = isoMatch[2];
    dd = isoMatch[3];

    return dd + mm + yyyy;
  }

  private toEnglishDigits(value: any): string {
    return String(value || "")
      .replace(/[\u0660-\u0669]/g, function(c) {
        return String(c.charCodeAt(0) - 0x0660);
      })
      .replace(/[\u06f0-\u06f9]/g, function(c) {
        return String(c.charCodeAt(0) - 0x06f0);
      });
  }

  getAmountInArabicWords() {
    var normalizedAmount = this.toEnglishDigits(this.amount);
    var numericAmount = Number(normalizedAmount);

    if (!normalizedAmount || isNaN(numericAmount) || numericAmount <= 0) {
      return this.getNestedValue(
        this.selectedTemplate,
        ["variables", "amount_text", "value"],
        ""
      );
    }

    try {
      return new Tafgeet(numericAmount, "EGP").parse();
    } catch (error) {
      return (
        this.getNestedValue(
          this.selectedTemplate,
          ["variables", "amount_text", "value"],
          ""
        ) || normalizedAmount
      );
    }
  }

  getAmountForChequeDisplay() {
    var typedAmount = String(this.amount || "").trim();
    var templateFormattedAmount;
    var templateRawAmount;

    if (typedAmount) {
      return this.formatAmountForTemplateDisplay(typedAmount);
    }

    templateFormattedAmount = String(
      this.getNestedValue(
        this.selectedTemplate,
        ["variables", "amount_text", "amountAfterFormat"],
        ""
      ) || ""
    ).trim();
    if (templateFormattedAmount) {
      return templateFormattedAmount;
    }

    templateRawAmount = String(
      this.getNestedValue(
        this.selectedTemplate,
        ["variables", "amount", "value"],
        ""
      ) || ""
    ).trim();
    if (templateRawAmount) {
      return this.formatAmountForTemplateDisplay(templateRawAmount);
    }

    return "";
  }

  private getNormalizedAmountForPayload(): number {
    var normalizedValue = this.normalizeDecimalString(this.amount);
    var parsedValue = Number(normalizedValue);

    if (!normalizedValue || !isFinite(parsedValue) || parsedValue <= 0) {
      return null;
    }

    return parsedValue;
  }

  private formatAmountForTemplateDisplay(value: string): string {
    var normalized = this.toEnglishDigits(value).replace(/[^\d.]/g, "");
    var parsedValue = Number(normalized);

    if (!isFinite(parsedValue) || parsedValue < 0) {
      return value;
    }

    return "#" + parsedValue.toFixed(2) + "#";
  }

  isLinerVisible(): boolean {
    return this.print_linear || !!String(this.linear_text || "").trim();
  }

  getLinearTextForPreview(): string {
    return String(this.linear_text || "").trim();
  }

  print() {
    var payload;
    var normalizedAmount;

    normalizedAmount = this.getNormalizedAmountForPayload();
    if (normalizedAmount === null) {
      swal("Amount must be a valid number", "", "error");
      return;
    }

    if (this.filteredChequeTemplates.length > 0 && !this.cheque_template_id) {
      swal("Please select cheque template", "", "error");
      return;
    }

    payload = {
      cheque_type: this.cheque_type,
      bank_id: this.bank_id,
      cheque_template_id: this.cheque_template_id,
      developer_id: this.cheque_type === "payable" ? null : this.developer_id,
      developer_name:
        this.cheque_type === "payable"
          ? String(this.developer_name || "").trim()
          : this.getDeveloperName(),
      amount: normalizedAmount,
      date: this.date,
      linear_text: this.linear_text,
      print_linear: this.print_linear,
      linear: {
        value: this.linear_text,
        selected: this.print_linear,
      },
    };

    this.is_loading = true;
    this.slimLoadingBarService.start();
    this.reservationService.printSingleCheque(payload).subscribe(
      (res: any) => {
        var windowPrt;

        this.slimLoadingBarService.complete();
        windowPrt = window.open(res.url, "", "");
        if (windowPrt) {
          windowPrt.focus();
        }
        this.is_loading = false;
      },
      () => {
        this.slimLoadingBarService.reset();
        this.is_loading = false;
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/print-single-cheque/print-single-cheque.component.ts